import React, { useCallback, useEffect, useRef, useState } from 'react';
import ScriptDialog from 'app/fuse-layouts/shared-components/ScriptDialog';
import OwnerSelectionCard from 'app/shared-components/OwnerSelectionCard';
import DcvCheckbox from '../components/DcvCheckbox';
import axios from 'axios';
import clsx from 'clsx';
import _ from '@lodash';
import Formsy from 'formsy-react';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import * as StackActions from 'app/main/stacks/store/actions/stacks.actions.js';
import * as MarketActions from 'app/main/marketplace/store/actions';
import * as ActionsDialog from 'app/store/actions';
import * as Actions from '../store/actions';
import { CLUSTER_STATES, DisabledFormWrapper } from 'app/main/util/clusterUtils';

// Material-UI core components
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fab, Icon, IconButton,
    InputBase, FormControl, FormControlLabel, MenuItem, Paper, Popover, Radio, RadioGroup,
    TextField, Typography, Table, TableBody, TableCell, TableHead, TableRow, Checkbox,
    InputAdornment, Menu, Card, CardHeader, CardContent, Box, Tooltip, Chip, DialogContentText,
    Select, InputLabel, Switch
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

// Fuse components and hooks
import { CheckboxFormsy, FuseAnimate, SelectFormsy, TextFieldFormsy } from '@fuse';
import { useForm } from '@fuse/hooks';
import { getClusterSettings, getOrgClusterSettings } from 'app/main/settings/store/actions';
import { API_BASE_URL } from '@config';
import { RESOURCE_MANAGER, IMAGE_TYPE, IMAGE_MODE } from "../../util/enums";
import { useInstanceAvailability } from '../hooks/useInstanceAvailability';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';

// Custom styles and utility functions
import useStyles from 'app/cluster-styles/useStyles';
import { ipValid, setinstancesVol, validationInputUlt, validationMessUlt } from '../../util/InputValidate';
import { formatZoneName, getZoneLabel } from '../util';

// Storage components
import ClusterStorageConfig from 'app/components/storage/ClusterStorageConfig';
import VolumeManager from '../components/VolumeManager';
import IbmVolumeDetailsDialog from '../components/IbmVolumeDetailsDialog';
import GlobalProvisionScriptsPreview from '../components/GlobalProvisionScriptsPreview';
import ClusterApplicationsSection from '../components/ClusterApplicationsSection';
import ClusterTagsInput from '../components/ClusterTagsInput';

const IbmCluster = props => {
    const dispatch = useDispatch();
    const classes = useStyles(props);
    const formRef = useRef(null);
    const pkgRef = useRef(null);

    // Select various states from the Redux store
    const clusterSettings = useSelector(({ settings }) => settings.settings.clusterSettings);
    const orgClusterSettings = useSelector(({ settings }) => settings.settings.orgClusterSettings);
    const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);
    const clustersArr = useSelector(({ clusters }) => clusters.cluster.list);
    const error = useSelector(({ clusters }) => clusters.cluster.error);
    const policies = useSelector(({ storage }) => storage.policies || []);
    const providers = useSelector(({ providers }) => providers.providers.list);
    const loading = useSelector(({ fuse }) => fuse.settings.loading);
    const credentials = useSelector(({ credentials }) => credentials.credential.list);
    const supportedOSs = useSelector(({ clusters }) => clusters.cluster.os_types);
    const marketplaceData = useSelector(({ marketStack }) => marketStack.marketStack.list);
    const selectedMarketStack = useSelector(({ marketStack }) => marketStack.marketStack.stack);
    const globalProvisionPreview = useSelector(({ globalProvision }) => globalProvision?.preview);
    const stacks = useSelector(({ stacks }) => stacks.stack.list);
    const currentUser = useSelector(({ auth }) => auth.user);
    const users = useSelector(({ auth }) => auth.user.users) || [];

    // Initialize form hook FIRST (used by marketplace data processing below)
    const { form, handleChange, setForm } = useForm(props.oldForm || null);


    // Marketplace data processing
    const market_id_value = selectedMarketStack?.id;
    const marketId = market_id_value || props.marketId;
    const isMarketStack = Boolean(selectedMarketStack);
    const currentMarketStack = marketplaceData?.find(item => item.id === marketId) || selectedMarketStack;
    
    const marketName = currentMarketStack?.name;
    const marketVersion = currentMarketStack?.version;
    const title = currentMarketStack ? (marketName + ' ' + marketVersion) : form?.name || 'New Cluster';
    const imageSrc = currentMarketStack?.logo
        ? `assets/images/logos/${currentMarketStack?.logo}`
        : `assets/images/providers/ibm.png`;

    // Marketplace form information
    const hasMarketplaceConfig = Boolean(currentMarketStack?.config)
    const marketplaceOSTypes = hasMarketplaceConfig ? currentMarketStack.config.os_types : null;
    const marketplaceInstanceIds = hasMarketplaceConfig ? currentMarketStack.config.instance_type : null;
    const marketplaceComputeIds = hasMarketplaceConfig ? currentMarketStack.config.computeNodeSize : null;
    const marketplaceManager = hasMarketplaceConfig ? currentMarketStack.config.resource_manager : null;

    // Marketplace app profile flags (data-driven behavior)
    const marketplaceSkipSubscription = hasMarketplaceConfig && currentMarketStack.config.skip_subscription === true;
    const marketplaceHideProvisionScript = hasMarketplaceConfig && currentMarketStack.config.hide_provision_script === true;
    const marketplaceAllowImageSelection = hasMarketplaceConfig && currentMarketStack.config.allow_image_selection === true;
    const marketplaceDisableGlobalScripts = hasMarketplaceConfig && currentMarketStack.config.disable_global_scripts === true;

    // Define local state variables 
    const [isFormValid, setIsFormValid] = useState(false);
    const [advanced, setAdvanced] = useState(false);
    const [zones, setZones] = useState([]);
    const [regions, setRegions] = useState([]);
    const [nodeInstanceSizes, setNodeInstanceSizes] = useState(null);
    const [serverInstanceSizes, setServerInstanceSizes] = useState([]);
    const [filteredCredentials, setFilteredCredentials] = useState([]);
    const [filteredStacks, setFilteredStacks] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState('');
    const [serverImageType, setServerImageType] = useState(IMAGE_TYPE.HEIDI_STACK);
    const [nodeImageTypes, setNodeImageTypes] = useState({});
    const [imageMode, setImageMode] = useState(IMAGE_MODE.UNIFIED);
    const [selectedRegionId, setSelectedRegionId] = useState('');
    const [selectedZoneId, setSelectedZoneId] = useState('');
    const [disableInputs, setDisableInputs] = useState(false);
    const [isScriptOpen, setScriptOpen] = useState(false);
    const [burstMode, setBurstMode] = useState(null);
    const [moabIsIntegrated, setMoabIsIntegrated] = useState(null);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [isVncTokenAuthentication, setIsVncTokenAuthentication] = useState(false);
    const [isVncRemoteClient, setIsVncRemoteClient] = useState(false);
    const [isDcvEnabled, setIsDcvEnabled] = useState(false);
    const [storageConfiguration, setStorageConfiguration] = useState(null);
    
    // Global provision scripts state (Feature 002 enhancement)
    const [excludedGlobalScripts, setExcludedGlobalScripts] = useState([]);
    
    // Cluster applications state (Feature: cluster-apps)
    const [clusterApplications, setClusterApplications] = useState([]);
    // Cluster tags state (Feature: cluster-tags)
    const [clusterTags, setClusterTags] = useState([]);
    const clusterTagsRef = useRef([]);
    // Keep ref in sync so useCallback always has latest tags
    useEffect(() => { clusterTagsRef.current = clusterTags; }, [clusterTags]);

    // Wrap setStorageConfiguration to add logging
    const handleStorageConfigurationChange = useCallback((newConfig) => {
        setStorageConfiguration(newConfig);
    }, []);
    
    // Volume management state
    const [volumeDropdownOpen, setVolumeDropdownOpen] = useState({});
    const volumeDropdownAnchorEl = useRef({});
    const [headNodeVolumeDropdownOpen, setHeadNodeVolumeDropdownOpen] = useState(false);
    const [headNodeVolumeDropdownAnchorEl, setHeadNodeVolumeDropdownAnchorEl] = useState(null);
    const [hostnameInfoAnchor, setHostnameInfoAnchor] = useState(null);
    const [pdsInfoPopoverAnchorEl, setPdsInfoPopoverAnchorEl] = useState(null);
    const pdsInfoPopoverOpen = Boolean(pdsInfoPopoverAnchorEl);
    const [volumeEditDialogOpen, setVolumeEditDialogOpen] = useState(false);
    const [currentEditingVolume, setCurrentEditingVolume] = useState(null);
    const [currentEditingNodeIndex, setCurrentEditingNodeIndex] = useState(null);
    
    // Head node volume details dialog state
    const [headDialogOpen, setHeadDialogOpen] = useState(false);
    const [headSelectedVolume, setHeadSelectedVolume] = useState(null);
    const [headSelectedStorageConfig, setHeadSelectedStorageConfig] = useState(null);
    const [headSelectedPolicy, setHeadSelectedPolicy] = useState(null);
    const [headIsEditable, setHeadIsEditable] = useState(false);
    
    const [disableScriptModification, setDisableScriptModification] = useState(false);
    const [providerVolsize, setProviderVolsize] = useState({ min: 100, max: 16000 });
    const [enableNodeInternetAccess, setEnableNodeInternetAccess] = useState(true);
    
    // IBM Capacity Reservation & Parallel Deployment
    const [enableCapacityReservation, setEnableCapacityReservation] = useState(false);
    const [capacityReservationParallelDeployment, setCapacityReservationParallelDeployment] = useState(false);

    // Instance availability hook for IBM
    // Note: IBM form uses form.server.region and form.server.zone (not form.data.network)
    var computeNodeInstanceTypes = [];
    if (form && form.data && form.data.node && form.data.node.group) {
        computeNodeInstanceTypes = form.data.node.group
            .map(function(g) { return g.instance_id; })
            .filter(Boolean);
    }

    const {
        zoneAvailability,
        isZoneAvailable,
        getMissingInstances,
        isLoading: isAvailabilityLoading,
        error: availabilityError,
        unableToVerify
    } = useInstanceAvailability({
        provider: 'ibm',
        credentialId: form && form.credential_id,
        region: form && form.server && form.server.region,
        headNodeInstance: form && form.server && form.server.instance_id,
        computeNodeInstances: computeNodeInstanceTypes,
        enabled: Boolean(form && form.credential_id && form.server && form.server.region)
    });

    // IBM zone-level availability (computed, not memoized)
    var ibmZoneAvailable = null;
    var ibmMissingInstances = [];
    if (!unableToVerify && form && form.server && form.server.zone && zoneAvailability) {
        var zoneId = form.server.zone;
        var zoneData = zoneAvailability[zoneId];
        if (zoneData) {
            ibmZoneAvailable = zoneData.available;
            ibmMissingInstances = zoneData.missingInstances || [];
        }
    }

    // Styles matching AWS form exactly
    const cardStyle = {
        marginBottom: '24px',
        boxShadow: '0 2px 10px 0 rgba(0,0,0,0.1)',
        borderRadius: '8px',
        overflow: 'visible',
        width: '100%'
    };

    const cardHeaderStyle = {
        paddingBottom: '8px',
        backgroundColor: '#f7f9fc'
    };

    const sectionTitleStyle = {
        fontSize: '16px',
        fontWeight: 600,
        color: '#0f2147'
    };

    const inputGroupStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '16px',
        marginBottom: '16px',
        alignItems: 'flex-start'
    };

    const tooltipIconStyle = {
        marginLeft: '8px',
        fontSize: '18px',
        color: '#9e9e9e',
        cursor: 'help'
    };

    const columnContainerStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '24px',
        width: '100%',
        minWidth: 0
    };

    const columnStyle = {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0, // This prevents flex items from overflowing
    };

    // Initialize component data
    useEffect(() => {
        dispatch(StackActions.getStacks());
        dispatch(getClusterSettings());
        dispatch(getOrgClusterSettings());
    }, [dispatch]);

    // Initialize form for new cluster
    useEffect(() => {
        if (props.type !== 'edit' && !form) {
            setForm({
                name: '',
                credential_id: '',
                stack_id: '',
                stack_name: '',
                server: {
                    region: '',
                    zone: '',
                    instance_id: '',
                    image_type: IMAGE_TYPE.HEIDI_STACK,
                    image_id: '',
                    image_name: ''
                },
                data: {
                    manager: 'torque',
                    provision: {
                        script: ''
                    },
                    server: {
                        volume_size: ''
                    },
                    network: {
                        enable_node_internet_access: true
                    },
                    node: {
                        volume_size: '',
                        group: [{
                            name: 'compute',
                            count: 1,
                            instance_id: '',
                            image_type: IMAGE_TYPE.HEIDI_STACK,
                            image_id: '',
                            image_name: ''
                        }]
                    },
                    moab: {
                        hostname: '',
                        ip: ''
                    },
                    share: 'nfs'
                },
                info: {
                    'os-type': '',
                    bursting: {
                        mode: 'off'
                    }
                }
            });
        }
    }, [props.type, form, setForm]);

    // Apply marketplace resource_manager override
    useEffect(() => {
        if (
            isMarketStack &&
            marketplaceManager &&
            form?.data &&
            form.data.manager !== marketplaceManager
        ) {
            setForm(prevForm => {
                if (!prevForm || !prevForm.data) return prevForm;
                return {
                    ...prevForm,
                    data: {
                        ...prevForm.data,
                        manager: marketplaceManager
                    }
                };
            });
        }
    }, [isMarketStack, marketplaceManager, form?.data?.manager, setForm]);

    // Initialize selectedOwner when editing an existing cluster
    useEffect(() => {
        if (props.type === 'edit' && form && form.user && form.user.length > 0) {
            setSelectedOwner(form.user[0]);
        }
    }, [props.type, form]);

    // Load cluster data from Redux when editing
    useEffect(() => {
        // Only run this for editing existing clusters, not for new clusters
        if (props.type === 'edit' && ((cluster && !form) || (cluster && form && cluster.id !== form.id))) {
            
            // Get server instance_id directly (no more instance_profile mapping)
            const serverInstanceId = cluster.data?.server?.instance_id || '';
            
            // Map node groups directly using instance_id
            const nodeGroups = cluster.data?.node?.group?.map((group, idx) => {

                
                // Destructure to exclude volume_size from individual groups
                const { volume_size, ...groupWithoutVolumeSize } = group;
                
                return {
                    ...groupWithoutVolumeSize,
                    count: group.count !== undefined ? group.count : 1, // Explicitly preserve count
                    instance_id: group.instance_id || '',
                    image_name: group.image_name || cluster.stack_name || ''
                };
            }) || [];
            
            // Ensure the provision object with script field is initialized
            const clusterWithProvision = {
                ...cluster,
                data: {
                    ...cluster.data,
                    manager: cluster.data?.manager || 'torque',
                    provision: {
                        ...cluster.data.provision || {},
                        script: cluster.data?.provision?.script || ''
                    },
                    server: {
                        ...cluster.data?.server,
                        instance_id: serverInstanceId,
                        instance_profile: serverInstanceId,
                        image_type: cluster.data?.server?.image_type || IMAGE_TYPE.HEIDI_STACK,
                        image_id: cluster.data?.server?.image_id || '',
                        image_name: cluster.data?.server?.image_name || cluster.stack_name || ''
                    },
                    moab: {
                        ...cluster.data?.moab,
                        hostname: cluster.data?.moab?.hostname || '',
                        ip: cluster.data?.moab?.ip || ''
                    },
                    node: {
                        ...cluster.data?.node,
                        group: nodeGroups
                    }
                },
                // Ensure server structure is complete
                server: {
                    ...cluster.server,
                    region: cluster.data?.network?.region_id || cluster.server?.region || '',
                    zone: cluster.data?.network?.zone_id || cluster.server?.zone || '',
                    instance_id: serverInstanceId,
                    image_type: cluster.data?.server?.image_type || IMAGE_TYPE.HEIDI_STACK,
                    image_id: cluster.data?.server?.image_id || '',
                    image_name: cluster.data?.server?.image_name || cluster.stack_name || ''
                },
                // Store stack_name at form root level for easy access
                stack_name: cluster.stack_name || cluster.data?.server?.image_name || '',
                stack_id: cluster.stack_id || '',
                info: {
                    ...cluster.info,
                    'os-type': cluster.info?.['os-type'] || cluster.data?.info?.['os-type'] || '',
                    bursting: cluster.info?.bursting || { mode: 'off' }
                }
            };

            setForm(clusterWithProvision);
            
            // Set burst mode immediately when loading cluster
            const loadedBurstMode = cluster.info?.bursting?.mode || 'off';
            setBurstMode(loadedBurstMode);
            
            // Load VNC settings
            setIsVncTokenAuthentication(cluster.info?.vncTokenAuthentication !== undefined ? cluster.info.vncTokenAuthentication : clusterSettings?.vncTokenAuthentication);
            setIsVncRemoteClient(cluster.info?.vncRemoteClient !== undefined ? cluster.info.vncRemoteClient : clusterSettings?.vncRemoteClient);

            // Initialize DCV enabled from cluster data or settings default
            if (cluster.info && cluster.info.dcvEnabled !== undefined) {
                setIsDcvEnabled(cluster.info.dcvEnabled);
            } else if (orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop || clusterSettings?.enableHighPerformanceRemoteDesktop) {
                setIsDcvEnabled(true);
            }
            
            // Load storage configuration if exists
            if (cluster.storageConfiguration) {
                setStorageConfiguration(cluster.storageConfiguration);
            }
            
            // Load excluded global scripts if exists (Feature 002 enhancement)
            // Check both locations - data.excludedGlobalScripts and root level excludedGlobalScripts
            const excluded = cluster.data?.excludedGlobalScripts || cluster.excludedGlobalScripts;
            if (excluded && excluded.length > 0) {
                setExcludedGlobalScripts(excluded);
            }
            
            // Load cluster applications if exists (Feature: cluster-apps)
            if (cluster.applications && cluster.applications.length > 0) {
                setClusterApplications(cluster.applications);
            }
            // Load cluster tags if exists (Feature: cluster-tags)
            if (cluster.tags && cluster.tags.length > 0) {
                setClusterTags(cluster.tags);
            }

            // Load network settings
            setEnableNodeInternetAccess(cluster.data?.network?.enable_node_internet_access !== undefined ? cluster.data.network.enable_node_internet_access : true);
            
            // Load capacity reservation settings
            setEnableCapacityReservation(cluster.data?.network?.enable_capacity_reservation ?? false);
            setCapacityReservationParallelDeployment(cluster.data?.network?.capacity_reservation_parallel_deployment ?? false);
        }
    }, [form, cluster, setForm, clusterSettings, props.type]);

    // Apply VNC settings from clusterSettings (for new clusters and as defaults)
    useEffect(() => {
        if (clusterSettings) {
            // For new clusters: form.info.vncTokenAuthentication is undefined, use clusterSettings
            // For existing clusters: form.info.vncTokenAuthentication has a value, use it
            const vncTokenAuth = form?.info?.vncTokenAuthentication;
            const vncRemote = form?.info?.vncRemoteClient;

            setIsVncTokenAuthentication(vncTokenAuth !== undefined ? vncTokenAuth : clusterSettings.vncTokenAuthentication);
            setIsVncRemoteClient(vncRemote !== undefined ? vncRemote : clusterSettings.vncRemoteClient);

            // Initialize DCV enabled from cluster data or settings default
            if (form && form.info && form.info.dcvEnabled !== undefined) {
                setIsDcvEnabled(form.info.dcvEnabled);
            } else if (orgClusterSettings?.clusterSettings?.enableHighPerformanceRemoteDesktop || clusterSettings?.enableHighPerformanceRemoteDesktop) {
                setIsDcvEnabled(true);
            }
        }
    }, [form, clusterSettings]);

    // Auto-exclude ALL global provision scripts when marketplace config has disable_global_scripts
    useEffect(() => {
        if (marketplaceDisableGlobalScripts && globalProvisionPreview?.configs?.length > 0) {
            const allScriptIds = [];
            globalProvisionPreview.configs.forEach(function(config) {
                if (config.scripts) {
                    config.scripts.forEach(function(script) {
                        allScriptIds.push(config._id + '-' + script._id);
                    });
                }
            });
            if (allScriptIds.length > 0) {
                setExcludedGlobalScripts(allScriptIds);
            }
        }
    }, [marketplaceDisableGlobalScripts, globalProvisionPreview?.configs]);

    // Pre-populate cluster applications from marketplace config for new marketplace clusters
    useEffect(() => {
        if (isMarketStack && currentMarketStack?.config?.applications?.length > 0 && clusterApplications.length === 0) {
            const apps = currentMarketStack.config.applications.map(function(app, index) {
                return {
                    id: app.id || (currentMarketStack.id || 'app') + '-' + index,
                    name: app.name || '',
                    url: app.url || '',
                    icon: app.icon || 'apps',
                    description: app.description || '',
                    category: app.category || 'general',
                    launchType: app.launchType || 'url',
                    instructions: app.instructions || '',
                    enabled: app.enabled !== false,
                    actionType: app.actionType || 'access-tab',
                    actionButtonLabel: app.actionButtonLabel || ''
                };
            });
            setClusterApplications(apps);
        }
    }, [isMarketStack, currentMarketStack]);

    // Set fast capacity validation default for new clusters
    useEffect(() => {
        if (props.type === 'new' && clusterSettings?.fastCapacityValidationDefault === true) {
            setEnableCapacityReservation(true);
            setCapacityReservationParallelDeployment(true);
        }
    }, [props.type, clusterSettings]);

    // Filter credentials and stacks for IBM provider
    useEffect(() => {
        const ibmCredentials = credentials.filter(cred => cred.provider === 'ibm');
        const ibmStacks = stacks.filter(stack => stack.provider === 'ibm');
        setFilteredCredentials(ibmCredentials);
        setFilteredStacks(ibmStacks);
    }, [credentials, stacks]);

    // Load regions and instances when component mounts
    useEffect(() => {
        const ibmProvider = (providers || []).find(p => p.id === 'ibm');
        if (ibmProvider) {
            if (ibmProvider.regions && ibmProvider.regions.length) {
                setRegions(ibmProvider.regions);
                setServerInstanceSizes(ibmProvider.instanceSizes || []);
                setNodeInstanceSizes(ibmProvider.instanceSizes || []);
            }
            // Set provider volume size limits
            if (ibmProvider.volsize) {
                setProviderVolsize(ibmProvider.volsize);
            }
        }
    }, [providers]);

    // Load zones when form region is populated (for edit mode)
    useEffect(() => {
        if (form && form.server && form.server.region && regions.length > 0) {
            const region = regions.find(r => r.region_id === form.server.region);
            if (region && region.zones) {
                setZones(region.zones);
                setSelectedRegionId(form.server.region);
            }
        }
        if (form && form.server && form.server.zone) {
            setSelectedZoneId(form.server.zone);
        }
    }, [form, regions]);

    // Handle form validation
    useEffect(() => {
        if (props.type === 'edit' && form && form.info && form.info.state != CLUSTER_STATES.DESTROYED) {
            setDisableInputs(true);
        }
    }, [props, form]);

    // Initialize node image types
    useEffect(() => {
        if (form && form.data && form.data.node && form.data.node.group) {
            const initialTypes = {};
            form.data.node.group.forEach((node, index) => {
                initialTypes[index] = node.image_type || IMAGE_TYPE.HEIDI_STACK;
            });
            setNodeImageTypes(initialTypes);
        }
    }, [form]);

    // Initialize server image type from form data
    useEffect(() => {
        if (form && form.data && form.data.server && form.data.server.image_type) {
            setServerImageType(form.data.server.image_type);
        } else if (form && form.server && form.server.image_type) {
            setServerImageType(form.server.image_type);
        }
    }, [form]);

    useEffect(() => {
        // Initialize imageMode from form data when editing
        if (form?.info?.imageMode) {
            setImageMode(form.info.imageMode);
        }
    }, [form?.info?.imageMode]);

    // Handle region selection
    const handleRegionChange = (event) => {
        const regionId = event.target.value;
        setSelectedRegionId(regionId);
        setForm(_.set({ ...form }, 'server.region', regionId));
        
        // Load zones for selected region
        const region = regions.find(r => r.region_id === regionId);
        if (region && region.zones) {
            setZones(region.zones);
        }
    };

    // Handle zone selection
    const handleZoneChange = (event) => {
        if (!form) return;
        const zoneId = event.target.value;
        setSelectedZoneId(zoneId);
        setForm(_.set({ ...form }, 'server.zone', zoneId));
    };

    // Add node group
    const addComputeNode = () => {
        if (!form?.data?.node?.group) return;
        
        const newNodeGroups = [...form.data.node.group, {
            name: `compute-${form.data.node.group.length + 1}`,
            count: 1,
            instance_id: '',
            image_type: IMAGE_TYPE.HEIDI_STACK,
            image_id: '',
            image_name: ''
        }];
        setForm(_.set({ ...form }, 'data.node.group', newNodeGroups));
    };

    // Remove node group
    const removeComputeNode = (index) => {
        if (!form?.data?.node?.group) return;
        
        const updatedGroups = [...form.data.node.group];
        updatedGroups.splice(index, 1);
        setForm(_.set({ ...form }, 'data.node.group', updatedGroups));
        
        // Clean up node image types
        const newNodeImageTypes = { ...nodeImageTypes };
        delete newNodeImageTypes[index];
        setNodeImageTypes(newNodeImageTypes);
    };

    // ===== VOLUME MANAGEMENT FUNCTIONS =====
    
    // Get available storage configurations
    const availableStorageConfigs = storageConfiguration?.configurations || [];
    
    // HEAD NODE VOLUME FUNCTIONS
    const addVolumeToHeadNode = (storageConfigId) => {
        const config = availableStorageConfigs.find(c => c.id === storageConfigId);
        if (!config) return;
        
        const headVolume = config.headNodeVolumes && config.headNodeVolumes[0];
        const volumeId = `head-volume-${Date.now()}`;
        
        const newVolume = {
            id: volumeId,
            storageConfigId: config.id,
            mountPath: headVolume?.mountPath || '/data',
            size: headVolume?.overrides?.capacity || null,
            policyId: headVolume?.policy || '',
            policyName: '', // Will be populated from policy lookup
            overrides: {
                capacity: headVolume?.overrides?.capacity || null,
                iops: headVolume?.overrides?.iops || null,
                throughput: headVolume?.overrides?.throughput || null
            },
            status: 'pending',
            nfsEnabled: false,
            nfsExportOptions: 'rw,sync,no_subtree_check,no_root_squash'
        };
        
        const updatedForm = _.cloneDeep(form);
        if (!updatedForm.data.server.attachedVolumes) {
            updatedForm.data.server.attachedVolumes = [];
        }
        updatedForm.data.server.attachedVolumes.push(newVolume);
        setForm(updatedForm);
        setHeadNodeVolumeDropdownOpen(false);
    };
    
    const removeVolumeFromHeadNode = (volumeId) => {
        const updatedForm = _.cloneDeep(form);
        if (updatedForm.data.server.attachedVolumes) {
            updatedForm.data.server.attachedVolumes = updatedForm.data.server.attachedVolumes.filter(
                vol => vol.id !== volumeId
            );
            setForm(updatedForm);
        }
    };
    
    const updateHeadNodeVolume = (updatedVolume) => {
        const updatedForm = _.cloneDeep(form);
        if (updatedForm.data.server.attachedVolumes) {
            const volumeIndex = updatedForm.data.server.attachedVolumes.findIndex(
                vol => vol.id === updatedVolume.id
            );
            if (volumeIndex !== -1) {
                updatedForm.data.server.attachedVolumes[volumeIndex] = updatedVolume;
                setForm(updatedForm);
            }
        }
        setVolumeEditDialogOpen(false);
        setCurrentEditingVolume(null);
    };
    
    // Head node volume dialog handlers
    const openHeadVolumeEditDialog = (volume) => {
        const storageConfig = availableStorageConfigs.find(config => config.id === volume.storageConfigId);
        const headVolume = storageConfig?.headNodeVolumes?.[0];
        const policyId = headVolume?.policy;
        const policy = policyId ? policies.find(p => p._id === policyId) : null;
        
        setHeadSelectedVolume(volume);
        setHeadSelectedStorageConfig(storageConfig || null);
        setHeadSelectedPolicy(policy);
        setHeadIsEditable(true);
        setHeadDialogOpen(true);
    };

    const closeHeadVolumeDialog = () => {
        setHeadDialogOpen(false);
        setHeadSelectedVolume(null);
        setHeadSelectedStorageConfig(null);
        setHeadSelectedPolicy(null);
        setHeadIsEditable(false);
    };

    const saveHeadVolumeDialog = (updatedVolume) => {
        updateHeadNodeVolume(updatedVolume);
        setHeadDialogOpen(false);
    };
    
    const handleHeadNodeVolumeDropdownOpen = (event) => {
        setHeadNodeVolumeDropdownAnchorEl(event.currentTarget);
        setHeadNodeVolumeDropdownOpen(true);
    };
    
    const handleHeadNodeVolumeDropdownClose = () => {
        setHeadNodeVolumeDropdownOpen(false);
    };
    
    const handleHeadVolumeConfigSelect = (configId) => {
        addVolumeToHeadNode(configId);
        handleHeadNodeVolumeDropdownClose();
    };
    
    // NODE GROUP VOLUME FUNCTIONS
    const addVolumeToNode = (nodeIndex, storageConfigId) => {
        const config = availableStorageConfigs.find(c => c.id === storageConfigId);
        if (!config) return;
        
        const headVolume = config.headNodeVolumes && config.headNodeVolumes[0];
        const volumeId = `node-${nodeIndex}-volume-${Date.now()}`;
        
        const newVolume = {
            id: volumeId,
            storageConfigId: config.id,
            mountPath: headVolume?.mountPath || '/data',
            size: headVolume?.overrides?.capacity || null,
            policyId: headVolume?.policy || '',
            policyName: '',
            overrides: {
                capacity: headVolume?.overrides?.capacity || null,
                iops: headVolume?.overrides?.iops || null,
                throughput: headVolume?.overrides?.throughput || null
            },
            status: 'pending'
        };
        
        const updatedForm = _.cloneDeep(form);
        const targetNodeGroup = updatedForm.data.node.group[nodeIndex];
        
        if (!targetNodeGroup.attachedVolumes) {
            targetNodeGroup.attachedVolumes = [];
        }
        targetNodeGroup.attachedVolumes.push(newVolume);
        setForm(updatedForm);
    };
    
    const removeVolumeFromNode = (nodeIndex, volumeId) => {
        const updatedForm = _.cloneDeep(form);
        const targetNodeGroup = updatedForm.data.node.group[nodeIndex];
        
        if (targetNodeGroup && targetNodeGroup.attachedVolumes) {
            targetNodeGroup.attachedVolumes = targetNodeGroup.attachedVolumes.filter(
                vol => vol.id !== volumeId
            );
            setForm(updatedForm);
        }
    };
    
    const handleVolumeUpdate = (nodeGroupIndex, updatedVolume) => {
        const updatedForm = _.cloneDeep(form);
        const targetNodeGroup = updatedForm.data.node.group[nodeGroupIndex];
        
        if (targetNodeGroup && targetNodeGroup.attachedVolumes) {
            const volumeIndex = targetNodeGroup.attachedVolumes.findIndex(
                vol => vol.id === updatedVolume.id
            );
            if (volumeIndex !== -1) {
                targetNodeGroup.attachedVolumes[volumeIndex] = updatedVolume;
                setForm(updatedForm);
            }
        }
        setVolumeEditDialogOpen(false);
        setCurrentEditingVolume(null);
    };
    
    const handleVolumeDropdownOpen = (event, nodeIndex) => {
        volumeDropdownAnchorEl.current[nodeIndex] = event.currentTarget;
        setVolumeDropdownOpen(prev => ({ ...prev, [nodeIndex]: true }));
    };
    
    const handleVolumeDropdownClose = (nodeIndex) => {
        setVolumeDropdownOpen(prev => ({ ...prev, [nodeIndex]: false }));
    };
    
    const handleVolumeConfigSelect = (nodeIndex, configId) => {
        addVolumeToNode(nodeIndex, configId);
        handleVolumeDropdownClose(nodeIndex);
    };

    // Handle close
    const handleClose = useCallback(() => {
        dispatch(ActionsDialog.closeDialog())
        dispatch(MarketActions.clearStack())
    }, [dispatch]);

    // Handle form submission
    const startSave = () => {
        if (!form) return;
        setSaveAttempted(true);
        
        // Validate stack is provided when using Heidi Stack image type
        if (serverImageType === IMAGE_TYPE.HEIDI_STACK && !form.stack_name) {
            dispatch(ActionsDialog.openDialog({
                children: (
                    <React.Fragment>
                        <DialogTitle>Validation Error</DialogTitle>
                        <DialogContent>
                            <Typography>Please select or enter a stack name when using Heidi Stack image type.</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => dispatch(ActionsDialog.closeDialog())} color="primary">
                                OK
                            </Button>
                        </DialogActions>
                    </React.Fragment>
                )
            }));
            return;
        }
        
        formRef.current.submit();
    };

    const handleSubmit = useCallback((model) => {
        if (!form?.server || !form?.data?.node?.group || !form?.info || !form?.data?.moab) {
            console.error('Form data incomplete for submission');
            return;
        }
        
        // Use the manager value directly from form (including 'none' if selected)
        const validManager = form.data.manager || 'torque';
    
        
        // Determine image_name based on image type
        let serverImageName = '';
        if (serverImageType === IMAGE_TYPE.HEIDI_STACK) {
            serverImageName = model.stack_name || form.stack_name || '';
        } else if (serverImageType === IMAGE_TYPE.CUSTOM) {
            serverImageName = form.server.image_name || '';
        }
        
        // Build cluster data following AWS structure - everything inside data object
        const clusterData = {
            name: model.name,
            displayName: model.displayName || '',
            credential_id: model.credential_id,
            stack_id: model.stack_id,
            stack_name: model.stack_name,
            provider: 'ibm',
            user: selectedOwner ? [selectedOwner] : (currentUser?.uid ? [currentUser.uid] : []),
            info: {
                'os-type': model['os-type'] || form.info['os-type'],
                bursting: {
                    mode: burstMode || 'off'
                },
                vncTokenAuthentication: isVncTokenAuthentication,
                vncRemoteClient: isVncRemoteClient,
                dcvEnabled: isDcvEnabled,
                imageMode: imageMode,
                isMarketplace: isMarketStack,
                market_id: marketId || null
            },
            data: {
                name: model.name,
                manager: validManager,
                keys: {},
                provision: form.data?.provision || {},
                packages: [],
                server: {
                    instance_id: model['server.instance_id'] || form.server.instance_id,
                    image_type: serverImageType,
                    image_id: form.server.image_id || '',
                    image_name: serverImageName,
                    volume_size: form.data?.server?.volume_size || ''
                },
                network: {
                    ...form.data?.network,
                    region_id: form.server.region,
                    zone_id: form.server.zone,
                    enable_node_internet_access: enableNodeInternetAccess,
                    enable_capacity_reservation: enableCapacityReservation,
                    capacity_reservation_parallel_deployment: capacityReservationParallelDeployment
                },
                moab: {
                    hostname: form.data.moab.hostname || '',
                    ip: form.data.moab.ip || ''
                },
                node: {
                    volume_size: form.data?.node?.volume_size || '',
                    group: form.data.node.group.map((group, index) => {
                        const nodeImageType = nodeImageTypes[index] || IMAGE_TYPE.HEIDI_STACK;
                        let nodeImageName = group.image_name || '';
                        
                        // If using Heidi Stack, use stack_name as image_name
                        if (nodeImageType === IMAGE_TYPE.HEIDI_STACK) {
                            nodeImageName = model.stack_name || form.stack_name || '';
                        }
                        
                        return {
                            ...group,
                            count: group.count !== undefined ? group.count : 1,
                            image_type: nodeImageType,
                            image_name: nodeImageName
                        };
                    })
                },
                share: form.data?.share || 'nfs'
            },
            features: form.features || []
        };

        // Always add storage configuration (even if disabled/empty) to ensure consistent state
        clusterData.storageConfiguration = storageConfiguration || { enabled: false, configurations: [] };

        // Add excluded global provision scripts at root level (Feature 002 enhancement)
        // Always include this field (even if empty) to ensure consistent database state
        clusterData.excludedGlobalScripts = excludedGlobalScripts || [];

        // Add cluster applications (Feature: cluster-apps)
        clusterData.applications = clusterApplications || [];
        // Add cluster tags (Feature: cluster-tags) - use ref to avoid stale closure
        var latestTags = clusterTagsRef.current || [];
        clusterData.tags = latestTags;

        // Dispatch create action
        dispatch(Actions.saveCluster(clusterData));

        // Close dialog if provided
        if (props.addCluster) {
            props.addCluster();
        }
    }, [dispatch, form?.data?.node?.group, form?.data?.manager, form?.data?.moab, form?.server, form?.stack_name, form?.info, nodeImageTypes, selectedOwner, currentUser, serverImageType, burstMode, props, storageConfiguration, enableNodeInternetAccess, enableCapacityReservation, capacityReservationParallelDeployment, isMarketStack, marketId, clusterApplications, excludedGlobalScripts]);

    // Handle edit/update submission
    const handleEdit = useCallback((model) => {
        if (!form?.server || !form?.data?.node?.group || !form?.info || !form?.data?.moab) {
            console.error('Form data incomplete for update');
            return;
        }
        
        // Use the manager value directly from form (including 'none' if selected)
        const validManager = form.data.manager || 'torque';
        
        // Reset deployment times for re-deployment
        if (form?.info?.lastDeployTime) form.info.lastDeployTime = 0;
        if (form?.info?.lastDestroyTime) form.info.lastDestroyTime = 0;
        
        // Determine image_name based on image type
        let serverImageName = '';
        if (serverImageType === IMAGE_TYPE.HEIDI_STACK) {
            serverImageName = model.stack_name || form.stack_name || '';
        } else if (serverImageType === IMAGE_TYPE.CUSTOM) {
            serverImageName = form.server.image_name || '';
        }
        
        // Get server instance_id (IBM now uses instance_id like all other providers)
        const serverInstanceId = model['server.instance_id'] || form.server.instance_id || form.data?.server?.instance_id;
        
        // Build cluster data for update
        const updatedClusterData = {
            ...form,
            name: model.name,
            credential_id: model.credential_id,
            stack_id: model.stack_id,
            stack_name: model.stack_name,
            info: {
                ...form.info,
                'os-type': model['os-type'] || form.info['os-type'],
                bursting: {
                    mode: burstMode || 'off'
                },
                vncTokenAuthentication: isVncTokenAuthentication,
                vncRemoteClient: isVncRemoteClient,
                dcvEnabled: isDcvEnabled,
                isMarketplace: isMarketStack,
                market_id: marketId || null
            },
            data: {
                ...form.data,
                name: model.name,
                manager: validManager,
                provision: form.data?.provision || {},
                server: {
                    ...form.data.server,
                    instance_id: serverInstanceId,
                    image_type: serverImageType,
                    image_id: form.server.image_id || '',
                    image_name: serverImageName,
                    volume_size: form.data?.server?.volume_size || ''
                },
                network: {
                    ...form.data?.network,
                    region_id: form.server.region,
                    zone_id: form.server.zone,
                    enable_node_internet_access: enableNodeInternetAccess,
                    enable_capacity_reservation: enableCapacityReservation,
                    capacity_reservation_parallel_deployment: capacityReservationParallelDeployment
                },
                moab: {
                    hostname: form.data.moab.hostname || '',
                    ip: form.data.moab.ip || ''
                },
                node: {
                    ...form.data.node,
                    volume_size: form.data?.node?.volume_size || '',
                    group: form.data.node.group.map((group, index) => {
                        const nodeImageType = nodeImageTypes[index] || IMAGE_TYPE.HEIDI_STACK;
                        let nodeImageName = group.image_name || '';
                        
                        // If using Heidi Stack, use stack_name as image_name
                        if (nodeImageType === IMAGE_TYPE.HEIDI_STACK) {
                            nodeImageName = model.stack_name || form.stack_name || '';
                        }
                        
                        
                        return {
                            name: group.name || `compute-${index + 1}`,
                            count: group.count !== undefined ? group.count : 1,
                            instance_id: group.instance_id,
                            image_type: nodeImageType,
                            image_id: group.image_id || '',
                            image_name: nodeImageName,
                            volume_size: group.volume_size,
                            group_node_prefix: group.group_node_prefix,
                            group_node_prefix_label: group.group_node_prefix_label,
                            zone_id: group.zone_id,
                            storageReference: group.storageReference,
                            nodeType: group.nodeType,
                            attachedVolumes: group.attachedVolumes || []
                        };
                    })
                }
            }
        };
        
        // IMPORTANT: Remove old storageConfiguration from spread if it exists
        delete updatedClusterData.storageConfiguration;

        // Always add storage configuration (even if disabled/empty) to ensure database update
        updatedClusterData.storageConfiguration = storageConfiguration || { enabled: false, configurations: [] };

        // Add excluded global provision scripts at root level (Feature 002 enhancement)
        if (excludedGlobalScripts && excludedGlobalScripts.length > 0) {
            updatedClusterData.excludedGlobalScripts = excludedGlobalScripts;
        } else {
            // Clear the array if no scripts are excluded
            updatedClusterData.excludedGlobalScripts = [];
        }

        // Add cluster applications (Feature: cluster-apps)
        updatedClusterData.applications = clusterApplications || [];
        // Add cluster tags (Feature: cluster-tags) - use ref to avoid stale closure
        var latestTags = clusterTagsRef.current || [];
        updatedClusterData.tags = latestTags;

        // Set imageMode in info object (matches schema)
        updatedClusterData.info.imageMode = imageMode;

        // Add owner selection if super-admin selected a different owner
        let ownerUsername;
        if (currentUser?.role && currentUser.role.includes('super-admin') && selectedOwner) {
            ownerUsername = selectedOwner;
        }

        dispatch(Actions.editCluster(updatedClusterData, [], ownerUsername))
            .then(function() {
                // Ensure tags are saved via dedicated endpoint as a fallback
                if (latestTags.length > 0 && updatedClusterData.id) {
                    dispatch(Actions.updateClusterTags(updatedClusterData.id, latestTags));
                }
            });
    }, [dispatch, form, serverImageType, nodeImageTypes, selectedOwner, currentUser, burstMode, storageConfiguration, enableNodeInternetAccess, enableCapacityReservation, capacityReservationParallelDeployment, imageMode, excludedGlobalScripts, isMarketStack, marketId, clusterApplications]);

    // Start edit process
    const startEdit = () => {
        if (!form) return;
        setSaveAttempted(true);
        
        // Validate stack is provided when using Heidi Stack image type
        if (serverImageType === IMAGE_TYPE.HEIDI_STACK && !form.stack_name) {
            dispatch(ActionsDialog.openDialog({
                children: (
                    <React.Fragment>
                        <DialogTitle>Validation Error</DialogTitle>
                        <DialogContent>
                            <Typography>Please select or enter a stack name when using Heidi Stack image type.</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => dispatch(ActionsDialog.closeDialog())} color="primary">
                                OK
                            </Button>
                        </DialogActions>
                    </React.Fragment>
                )
            }));
            return;
        }
        
        formRef.current.submit();
    };

    // Handle node group changes
    const updateNodeGroup = (index, field, value) => {
        if (!form?.data?.node?.group) return;
                
        const updatedGroups = [...form.data.node.group];
        updatedGroups[index] = { ...updatedGroups[index], [field]: value };
        
        // Force a complete state update by creating a new form object
        const newForm = {
            ...form,
            data: {
                ...form.data,
                node: {
                    ...form.data.node,
                    group: updatedGroups
                }
            }
        };
        setForm(newForm);
        
    };

    // Helper function for default OS type
    const getDefaultOsType = () => {
        // If marketplace restricts OS types, prioritize the marketplace OS
        if (marketplaceOSTypes && supportedOSs) {
            const allowedOSs = supportedOSs.filter(os => marketplaceOSTypes.includes(os));
            if (allowedOSs.length > 0) {
                if (form?.info?.['os-type'] && allowedOSs.includes(form.info['os-type'])) {
                    return form.info['os-type'];
                }
                return allowedOSs[0];
            }
        }
        if (supportedOSs && form?.info && supportedOSs.includes(form.info['os-type'])) {
            return form.info['os-type'];
        }
        return supportedOSs?.[0] || '';
    };

    // Helper functions for form data handling
    const setDataObj = (target) => {
        if (!form) return;
        setForm(_.set({ ...form }, `data.${target.name}`, target.value));
    };

    const setClusterInfo = (event) => {
        if (!form) return;
        const { name, value } = event.target;
        setForm(_.set({ ...form }, `data.moab.${name}`, value));
    };

    // Handle script upload
    const handleUpload = e => {
        const file = e.target.files[0];
        if (!file) return;

        //reset value to '' so that the same file can be uploaded if removed
        e.target.value = '';

        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = () => {
            setForm(
                _.set({ ...form }, 'data', { ...form.data, provision: { ...form.data.provision, script: reader.result } })
            );
        };
    };

    // Handle script deletion
    const handleDeleteScript = () => {
        setForm(_.set({ ...form }, 'data', { ...form.data, provision: { ...form.data.provision, script: '' } }));
    };

    // Set server object
    const setServerObj = (target) => {
        if (!form) return;
        setForm(_.set({ ...form }, `data.server.${target.name}`, target.value));
    };

    // Set node object
    const setNodeObj = (target) => {
        if (!form) return;
        setForm(_.set({ ...form }, `data.node.${target.name}`, target.value));
    };

    // Check if ODDCCD bursting is enabled
    const isODDCBurstingSelectionEnabled = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/moab/isIntegrated`)
            return response.data?.integrated || false;
        } catch (error) {
            console.warn('Failed to check ODDCCD integration status:', error);
            return false;
        }
    };

    // Effect to load OS types for IBM provider
    useEffect(() => {
        dispatch(Actions.provider_os_types("ibm"));
    }, [dispatch]);

    // Auto-select OS type when marketplace app restricts supported OS types
    useEffect(() => {
        if (marketplaceOSTypes && supportedOSs && supportedOSs.length > 0 && form) {
            const allowedOSs = supportedOSs.filter(os => marketplaceOSTypes.includes(os));
            if (allowedOSs.length > 0 && !allowedOSs.includes(form.info?.['os-type'])) {
                setForm(_.set({ ...form }, 'info.os-type', allowedOSs[0]));
            }
        }
    }, [marketplaceOSTypes, supportedOSs]);

    // Effect to check MOAB integration status
    useEffect(() => {
        const checkMoabIntegration = async () => {
            const isIntegrated = await isODDCBurstingSelectionEnabled();
            setMoabIsIntegrated(isIntegrated);
        };
        
        checkMoabIntegration();
    }, []);

    // Effect to initialize burst mode for NEW clusters only
    useEffect(() => {
        if (burstMode !== null) return
        
        // Skip this for edit mode - burst mode is set when cluster loads
        if (props.type === 'edit') {
            return;
        }
        
        // For new clusters, check database value first before setting default
        if (form?.info?.bursting?.mode) {
            setBurstMode(form.info.bursting.mode)
        } else {
            !currentMarketStack ? 
                setBurstMode('off') : setBurstMode('off');
        }
    }, [form, burstMode, currentMarketStack, props.type]);

    // Effect to update burst mode in form
    useEffect(() => {
        if (burstMode === null) return
        
        setForm(prevForm => {
            const updatedForm = { ...prevForm };
            if (!updatedForm.info) updatedForm.info = {};
            if (!updatedForm.info.bursting) updatedForm.info.bursting = {};
            updatedForm.info.bursting.mode = burstMode;
            return updatedForm;
        });
    }, [burstMode]);

    // Effect to handle manager changes
    useEffect(() => {
        if (form?.data?.manager && form.data.manager !== RESOURCE_MANAGER.TORQUE && burstMode !== 'off') {
            setBurstMode('off');
        }
    }, [form?.data?.manager, burstMode]);

    useEffect(() => {
        if (form?.data?.manager && form.data.manager !== RESOURCE_MANAGER.TORQUE) {
            // Clear moab fields when not using torque
            setForm(_.set({ ...form }, 'data.moab.hostname', ''));
            setForm(_.set({ ...form }, 'data.moab.ip', ''));
        }
    }, [form?.data?.manager]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClose]);

    // Download marketplace provision script if configured
    useEffect(() => {
        const downloadProvisionScript = async () => {
            if (
                currentMarketStack?.config?.provision_script && 
                form?.data?.provision &&
                !form.data.provision.script // Only download if no script already exists
            ) {
                dispatch(ActionsDialog.toggleLoading());
                try {
                    const scriptContent = await dispatch(MarketActions.getProvisionScript(currentMarketStack.config.provision_script));
                    setForm(_.set({ ...form }, 'data', { 
                        ...form.data, 
                        provision: { 
                            ...form.data.provision, 
                            script: scriptContent 
                        } 
                    }));
                } catch (error) {
                    console.error("Error downloading provision script:", error);
                } finally {
                    dispatch(ActionsDialog.toggleLoading());
                }
            }
        };
        
        downloadProvisionScript();
    }, [currentMarketStack, form, dispatch, setForm]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <div className="flex flex-1 w-full items-center justify-between">
                            <div className="flex flex-col max-w-full">
                                <div className="flex items-center max-w-full">
                                    <FuseAnimate animation="transition.expandIn" delay={300}>
                                        <img
                                            id="ibm_provider-logo"
                                            className="w-32 sm:w-48 mr-8 sm:mr-16 rounded"
                                            src={imageSrc} alt="Provider Logo"
                                        />
                                    </FuseAnimate>
                                    <div className="flex flex-col min-w-0">
                                        <FuseAnimate animation="transition.slideLeftIn" delay={300}>
                                            <Typography id="ibm_cluster-name-header" className="text-16 text-white sm:text-20 truncate">
                                                {title}
                                            </Typography>
                                        </FuseAnimate>
                                        <FuseAnimate animation="transition.slideLeftIn" delay={300}>
                                            <Typography id="ibm_provider-name-header" className="text-white" variant="caption">
                                                {`IBM Cloud`}
                                            </Typography>
                                        </FuseAnimate>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <Button
                                    className="whitespace-no-wrap mr-2"
                                    id="ibm_advanced-btn"
                                    variant="contained"
                                    disabled={loading}
                                    onClick={() => setAdvanced(!advanced)}
                                >
                                    {advanced ? "Basic" : "Advanced"}
                                </Button>
                                {props.type === 'new' && (
                                    <Button
                                        className="whitespace-no-wrap mx-2"
                                        id="ibm_save-btn"
                                        disabled={!isFormValid}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            startSave();
                                            dispatch(MarketActions.clearStack());
                                        }}
                                    >
                                        {'Save'}
                                    </Button>
                                )}
                                {props.type === 'edit' && (
                                    <Button
                                        className="whitespace-no-wrap mx-2"
                                        id="ibm_update-btn"
                                        disabled={
                                            !isFormValid || 
                                            loading ||
                                            (form?.info?.state && form.info.state !== CLUSTER_STATES.DESTROYED)
                                        }
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            startEdit();
                                            dispatch(MarketActions.clearStack());
                                        }}
                                        title={form?.info?.state !== CLUSTER_STATES.DESTROYED ? 
                                            `Cluster must be destroyed to update. Current state: ${form?.info?.state}` : 
                                            'Update cluster configuration'}
                                    >
                                        Update
                                    </Button>
                                )}
                                <Button
                                    className="whitespace-no-wrap ml-2"
                                    id="ibm_close-btn"
                                    variant="contained"
                                    onClick={handleClose}
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    </DialogTitle>

                    <DialogContent className={clsx(classes.content, 'p-16 -mr-200 ml-20')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <Formsy
                                onValidSubmit={props.type === 'edit' ? handleEdit : handleSubmit}
                                onValid={() => setIsFormValid(true)}
                                onInvalid={() => setIsFormValid(false)}
                                ref={formRef}
                                className="!w-full"
                            >
                                {/* Owner Selection Card - Only for Super Admin in Advanced Mode */}
                                <OwnerSelectionCard
                                    value={selectedOwner}
                                    onChange={setSelectedOwner}
                                    label="Cluster Owner"
                                    title="Cluster Owner"
                                    showInAdvancedMode={true}
                                    advanced={advanced}
                                    disabled={props.type === 'edit' && form && form.info && form.info.state !== CLUSTER_STATES.DESTROYED}
                                />

                                {/* Basic Information - Full Width */}
                                <Card style={cardStyle}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box display="flex" alignItems="center">
                                                    <Icon style={{marginRight: '8px', color: '#2196F3'}}>info</Icon>
                                                    <Typography style={sectionTitleStyle}>Basic Information</Typography>
                                                </Box>
                                                {props.type === 'edit' && form?.id && (
                                                    <Typography style={{color: '#666', fontSize: '14px', fontWeight: 'normal'}}>
                                                        ID: {form.id}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        style={cardHeaderStyle}
                                    />
                                    <CardContent>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
                                            {/* Left Column - Cluster Name & OS */}
                                            <div style={{ width: '48%' }}>
                                                <div style={{ display: 'flex', gap: '16px' }}>
                                                    <TextFieldFormsy
                                                        className="flex-1"
                                                        type="text"
                                                        name="displayName"
                                                        label="Cluster Name"
                                                        value={form?.displayName}
                                                        onChange={handleChange}
                                                        required
                                                        placeholder="Cluster Name"
                                                        variant="outlined"
                                                        validations={{
                                                            doesNotExceedMaxLength: (formValues, fieldValue) =>
                                                                !fieldValue || fieldValue.length <= 128 || 'Display name cannot exceed 128 characters.',
                                                        }}
                                                        validationErrors={{
                                                            doesNotExceedMaxLength: 'Display name cannot exceed 128 characters.',
                                                        }}
                                                    />
                                                    <SelectFormsy
                                                        className="flex-1"
                                                        id="ibm_os-type"
                                                        name="os-type"
                                                        label="Operating System"
                                                        type="select"
                                                        variant="outlined"
                                                        value={getDefaultOsType()}
                                                        onChange={e => {
                                                            setForm(_.set({ ...form }, 'info.os-type', e.target.value));
                                                        }}
                                                        required
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: { maxHeight: 300 }
                                                            }
                                                        }}
                                                        disabled={disableInputs || (marketplaceOSTypes && supportedOSs?.filter(os => marketplaceOSTypes.includes(os)).length === 1)}
                                                    >
                                                        {supportedOSs
                                                            ?.filter(os => !marketplaceOSTypes || marketplaceOSTypes.includes(os))
                                                            .map((os, index) => (
                                                                <MenuItem
                                                                    id={`os-${os}`}
                                                                    key={index}
                                                                    value={os}
                                                                >
                                                                    {os}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </SelectFormsy>
                                                </div>
                                                <div style={{ display: 'flex', gap: '16px', marginTop: '16px', alignItems: 'flex-start' }}>
                                                    <TextFieldFormsy
                                                        className="flex-1"
                                                        type="text"
                                                        name="name"
                                                        label="Hostname"
                                                        value={form?.name || ''}
                                                        onChange={handleChange}
                                                        helperText={undefined}
                                                        disabled={props.type !== 'edit' || disableInputs}
                                                        required={props.type === 'edit'}
                                                        InputProps={props.type !== 'edit' ? {
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <Icon
                                                                        style={{ color: '#2196F3', cursor: 'pointer' }}
                                                                        onClick={(e) => setHostnameInfoAnchor(e.currentTarget)}
                                                                    >info_outline</Icon>
                                                                    <Popover
                                                                        open={Boolean(hostnameInfoAnchor)}
                                                                        anchorEl={hostnameInfoAnchor}
                                                                        onClose={() => setHostnameInfoAnchor(null)}
                                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                                    >
                                                                        <Box p={2} style={{ maxWidth: '300px' }}>
                                                                            <Typography variant="body2">
                                                                                The hostname is auto-generated from your cluster name when saved. You can customize it by editing the cluster afterwards.
                                                                            </Typography>
                                                                        </Box>
                                                                    </Popover>
                                                                </InputAdornment>
                                                            )
                                                        } : undefined}
                                                        validations={props.type === 'edit' ? {
                                                            matchRegexp: /^[a-zA-Z0-9]([a-zA-Z0-9_-]{0,28}[a-zA-Z0-9])?$/,
                                                            maxLength: 30
                                                        } : undefined}
                                                        validationErrors={props.type === 'edit' ? {
                                                            matchRegexp: "Must start and end with letter/number, contain only letters, numbers, hyphens, underscores (max 30 chars)",
                                                            maxLength: "Maximum 30 characters allowed"
                                                        } : undefined}
                                                        variant="outlined"
                                                    />
                                                    <ClusterTagsInput
                                                        tags={clusterTags}
                                                        onChange={setClusterTags}
                                                        style={{ flex: 1 }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <div style={{
                                                width: '2px',
                                                backgroundColor: '#e0e0e0',
                                                margin: '10px 0',
                                                alignSelf: 'stretch'
                                            }} />

                                            {/* Right Column - Stack & Image Type */}
                                            <div style={{ width: '48%' }}>
                                                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' }}>
                                                    <Autocomplete
                                                        className="flex-1"
                                                        freeSolo
                                                        disabled={disableInputs}
                                                        options={filteredStacks}
                                                        getOptionLabel={(option) => {
                                                            if (typeof option === 'string') return option;
                                                            // Display: "prefix - description" or just "prefix"
                                                            return option.data?.prefix 
                                                                ? `${option.data.prefix}${option.description ? ` - ${option.description}` : ''}`
                                                                : (option.name || '');
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={"Stack"}
                                                                placeholder="Your Stack Name or Select Stack"
                                                                required={serverImageType === IMAGE_TYPE.HEIDI_STACK}
                                                                variant="outlined"
                                                                disabled={disableInputs}
                                                                error={saveAttempted && serverImageType === IMAGE_TYPE.HEIDI_STACK && !form.stack_name}
                                                                helperText={saveAttempted && serverImageType === IMAGE_TYPE.HEIDI_STACK && !form.stack_name ? "Stack is required for Heidi Stack image type" : ""}
                                                            />
                                                        )}
                                                        onInputChange={(event, newInputValue, reason) => {
                                                            // Capture typed input in real-time
                                                            if (reason === 'input') {
                                                                setForm({
                                                                    ...form,
                                                                    stack_name: newInputValue,
                                                                    server: {
                                                                        ...form.server,
                                                                        image_name: newInputValue
                                                                    }
                                                                });
                                                            }
                                                        }}
                                                        onChange={(event, newValue) => {
                                                            if (typeof newValue === 'string') {
                                                                // User typed a custom value
                                                                setForm({
                                                                    ...form,
                                                                    stack_name: newValue,
                                                                    server: {
                                                                        ...form.server,
                                                                        image_name: newValue
                                                                    }
                                                                });
                                                            } else if (newValue) {
                                                                // User selected from dropdown - use prefix, not name
                                                                const stackPrefix = newValue.data?.prefix || newValue.name;
                                                                setForm({
                                                                    ...form,
                                                                    stack_id: newValue.id,
                                                                    stack_name: stackPrefix,
                                                                    server: {
                                                                        ...form.server,
                                                                        image_name: stackPrefix
                                                                    }
                                                                });
                                                            } else {
                                                                // Clear stack when selection is removed
                                                                setForm({
                                                                    ...form,
                                                                    stack_id: '',
                                                                    stack_name: '',
                                                                    server: {
                                                                        ...form.server,
                                                                        image_name: ''
                                                                    }
                                                                });
                                                            }
                                                        }}
                                                        value={form.stack_name || ''}
                                                    />
                                                    
                                                    <SelectFormsy
                                                        className="flex-1"
                                                        id="ibm_server_image_type"
                                                        name="server_image_type"
                                                        label="Image Type"
                                                        type="select"
                                                        variant="outlined"
                                                        value={serverImageType}
                                                        onChange={(e) => setServerImageType(e.target.value)}
                                                        style={{ width: '180px' }}
                                                        disabled={disableInputs}
                                                    >
                                                        <MenuItem value={IMAGE_TYPE.HEIDI_STACK}>Heidi Stack</MenuItem>
                                                        <MenuItem value={IMAGE_TYPE.CUSTOM}>Custom Image</MenuItem>
                                                    </SelectFormsy>
                                                </div>
                                                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginTop: '16px' }}>
                                                    <SelectFormsy
                                                        className={clsx(classes.input)}
                                                        id="ibm_image_mode"
                                                        name="image_mode"
                                                        label="Image Mode"
                                                        type="select"
                                                        variant="outlined"
                                                        value={imageMode}
                                                        onChange={e => setImageMode(e.target.value)}
                                                        style={{ width: '220px' }}
                                                        disabled={disableInputs}
                                                    >
                                                        <MenuItem value={IMAGE_MODE.UNIFIED}>Unified (Single Image)</MenuItem>
                                                        <MenuItem value={IMAGE_MODE.MULTI_IMAGE}>Multi-Image (Legacy)</MenuItem>
                                                    </SelectFormsy>
                                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px', maxWidth: '300px' }}>
                                                        {imageMode === IMAGE_MODE.UNIFIED
                                                            ? 'Single unified image for head and compute nodes'
                                                            : 'Separate images for head node and compute nodes'}
                                                    </Typography>
                                                </div>

                                                {serverImageType === IMAGE_TYPE.CUSTOM && (
                                                    <TextFieldFormsy
                                                        name="server.image_id"
                                                        label="Head Node Custom Image ID"
                                                        value={form.server.image_id}
                                                        onChange={handleChange}
                                                        variant="outlined"
                                                        disabled={disableInputs}
                                                        fullWidth
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Two-column layout: Infrastructure and Resource Manager */}
                                <div style={columnContainerStyle}>
                                    {/* Left Column - Infrastructure */}
                                    <div style={columnStyle}>
                                        {/* Infrastructure Card */}
                                <Card style={cardStyle}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center">
                                                <Icon style={{marginRight: '8px', color: '#2196F3'}}>cloud</Icon>
                                                <Typography style={sectionTitleStyle}>Infrastructure</Typography>
                                                <Tooltip title="The available Regions, Availability Zones, and Instance Sizes may vary based on your provider account status.">
                                                    <Icon style={tooltipIconStyle}>help_outline</Icon>
                                                </Tooltip>
                                            </Box>
                                        }
                                        action={
                                            form && form.server && form.server.zone ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading}
                                                    isAvailable={ibmZoneAvailable}
                                                    missingInstances={ibmMissingInstances}
                                                    error={availabilityError}
                                                    unableToVerify={unableToVerify}
                                                />
                                            ) : null
                                        }
                                        style={cardHeaderStyle}
                                    />
                                    <CardContent>
                                        {/* First Row: Credential | Region | Zone */}
                                        <div style={inputGroupStyle}>
                                            <SelectFormsy
                                                className="flex-1"
                                                name="credential_id"
                                                label="IBM Cloud Credentials"
                                                value={form.credential_id}
                                                onChange={handleChange}
                                                required
                                                variant="outlined"
                                                disabled={disableInputs}
                                            >
                                                {filteredCredentials.map((credential) => (
                                                    <MenuItem key={credential.id} value={credential.id}>
                                                        {credential.name}
                                                    </MenuItem>
                                                ))}
                                            </SelectFormsy>

                                            <TextField
                                                select
                                                className="flex-1"
                                                name="server.region"
                                                label="Region"
                                                value={form.server.region || ''}
                                                onChange={handleRegionChange}
                                                required
                                                variant="outlined"
                                                disabled={disableInputs}
                                            >
                                                {regions.map((region) => (
                                                    <MenuItem key={region.region_id} value={region.region_id}>
                                                        {region.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <TextField
                                                select
                                                className="flex-1"
                                                name="server.zone"
                                                label="Zone"
                                                value={form.server.zone || ''}
                                                onChange={handleZoneChange}
                                                required
                                                variant="outlined"
                                                disabled={disableInputs || !selectedRegionId}
                                            >
                                                {zones.map((zone) => (
                                                    <MenuItem key={zone.zone_id} value={zone.zone_id}>
                                                        {zone.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </div>

                                        {/* Second Row: Head Node Size */}
                                        <div style={inputGroupStyle}>
                                            <SelectFormsy
                                                className="flex-1"
                                                name="server.instance_id"
                                                label="Head Node Size *"
                                                value={form.server.instance_id}
                                                onChange={handleChange}
                                                required
                                                variant="outlined"
                                                disabled={disableInputs}
                                            >
                                                {serverInstanceSizes.map((instance) => (
                                                    <MenuItem key={instance.instance_id || instance.id} value={instance.instance_id || instance.id}>
                                                        {instance.name} - {instance.vcpus || instance.cores} vCPUs, {instance.memory} GB RAM
                                                    </MenuItem>
                                                ))}
                                            </SelectFormsy>
                                        </div>
                                    </CardContent>
                                </Card>
                                    </div>

                                    {/* Right Column - Resource Manager Configuration */}
                                    <div style={columnStyle}>
                                        <Card style={cardStyle}>
                                            <CardHeader
                                                title={
                                                    <Box display="flex" alignItems="center">
                                                        <Icon style={{marginRight: '8px', color: '#2196F3'}}>settings_applications</Icon>
                                                        <Typography style={sectionTitleStyle}>Resource Manager Configuration</Typography>
                                                    </Box>
                                                }
                                                style={cardHeaderStyle}
                                            />
                                            <CardContent>
                                                {/* First Row: Manager | Hostname | IP */}
                                                <div style={inputGroupStyle}>
                                                    <SelectFormsy
                                                        className="flex-1"
                                                        id="ibm_manager"
                                                        name="manager"
                                                        label="Manager"
                                                        type="select"
                                                        variant="outlined"
                                                        value={form?.data?.manager || 'torque'}
                                                        onChange={e => setDataObj(e.target)}
                                                        required
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: { maxHeight: 200 }
                                                            }
                                                        }}
                                                        disabled={disableInputs}
                                                    >   
                                                        <MenuItem key="none" id="ibm_none" value="none">
                                                            None
                                                        </MenuItem>
                                                        <MenuItem key="slurm" id="ibm_slurm" value="slurm">
                                                            Slurm
                                                        </MenuItem>
                                                        <MenuItem key="torque" id="ibm_torque" value="torque">
                                                            Torque
                                                        </MenuItem>
                                                    </SelectFormsy>

                                                    <TextField
                                                        className="flex-1"
                                                        id="ibm_hostname"
                                                        name="hostname"
                                                        label="Moab Hostname"
                                                        type="text"
                                                        variant="outlined"
                                                        value={form?.data?.moab?.hostname || ''}
                                                        required={form?.data?.manager === RESOURCE_MANAGER.TORQUE}
                                                        disabled={form?.data?.manager !== RESOURCE_MANAGER.TORQUE || disableInputs}
                                                        placeholder="Moab Hostname"
                                                        onChange={e => setClusterInfo(e)}
                                                        error={saveAttempted && form?.data?.manager === RESOURCE_MANAGER.TORQUE && (!form?.data?.moab?.hostname || form.data.moab.hostname.trim() === '')}
                                                        InputProps={{
                                                            readOnly: form?.data?.manager !== RESOURCE_MANAGER.TORQUE,
                                                            style: {
                                                                pointerEvents: form?.data?.manager !== RESOURCE_MANAGER.TORQUE ? 'none' : 'auto'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: {
                                                                color: form?.data?.manager !== RESOURCE_MANAGER.TORQUE ? 'rgba(0, 0, 0, 0.38)' : 'inherit'
                                                            }
                                                        }}
                                                    />

                                                    <TextField
                                                        className="flex-1"
                                                        id="ibm_ip"
                                                        name="ip"
                                                        label="Moab IP"
                                                        type="text"
                                                        variant="outlined"
                                                        value={form?.data?.moab?.ip || ''}
                                                        required={form?.data?.manager === RESOURCE_MANAGER.TORQUE}
                                                        disabled={form?.data?.manager !== RESOURCE_MANAGER.TORQUE || disableInputs}
                                                        placeholder="Moab IP"
                                                        onChange={e => setClusterInfo(e)}
                                                        InputProps={{
                                                            readOnly: form?.data?.manager !== RESOURCE_MANAGER.TORQUE,
                                                            style: {
                                                                pointerEvents: form?.data?.manager !== RESOURCE_MANAGER.TORQUE ? 'none' : 'auto'
                                                            }
                                                        }}
                                                        inputProps={{
                                                            style: {
                                                                color: form?.data?.manager !== RESOURCE_MANAGER.TORQUE ? 'rgba(0, 0, 0, 0.38)' : 'inherit'
                                                            }
                                                        }}
                                                        error={Boolean(
                                                            saveAttempted && form?.data?.manager === RESOURCE_MANAGER.TORQUE && (
                                                                !form?.data?.moab?.ip || 
                                                                form.data.moab.ip.trim() === '' ||
                                                                (form.data.moab.ip && !ipValid(form.data.moab.ip))
                                                            )
                                                        )}
                                                    />
                                                </div>
                                                
                                                {/* Second Row: Bursting Configuration */}
                                                <Box mt={2} sx={form.data.manager !== RESOURCE_MANAGER.TORQUE ? { opacity: 0.6 } : {}}>
                                                    <Typography variant="subtitle2" style={{marginBottom: '8px'}}>Bursting Configuration:</Typography>
                                                    <FormControl component="fieldset">
                                                        <RadioGroup
                                                            row
                                                            id="ibm_burstOnDeploy"
                                                            name="burstOnDeploy"
                                                            value={burstMode}
                                                            onChange={e => {
                                                                const mode = e.target.value
                                                                setBurstMode(mode)
                                                            }}
                                                        >
                                                            <FormControlLabel 
                                                                value="off" 
                                                                id="ibm_radio-off" 
                                                                control={<Radio color="primary" />} 
                                                                label="Off" 
                                                                disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}
                                                            />
                                                            <FormControlLabel 
                                                                value="min" 
                                                                id="ibm_radio-min" 
                                                                control={<Radio color="primary" />} 
                                                                label="Min" 
                                                                disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}
                                                            />
                                                            <FormControlLabel 
                                                                value="max" 
                                                                id="ibm_radio-max" 
                                                                control={<Radio color="primary" />} 
                                                                label="Max" 
                                                                disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}
                                                            />
                                                            <FormControlLabel 
                                                                value="all" 
                                                                id="ibm_radio-all" 
                                                                control={<Radio color="primary" />} 
                                                                label="All" 
                                                                disabled={form.data.manager !== RESOURCE_MANAGER.TORQUE}
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>

                                {/* Advanced Options: Remote Desktop Settings and Provisioning Script (two columns) */}
                                {advanced && (
                                    <div style={columnContainerStyle}>
                                        {/* Left column - Remote Desktop Settings */}
                                        <div style={columnStyle}>
                                            <Card style={{...cardStyle, height: '100%'}}>
                                                <CardHeader
                                                    title={
                                                        <Box display="flex" alignItems="center">
                                                            <Icon style={{marginRight: '8px', color: '#2196F3'}}>desktop_windows</Icon>
                                                            <Typography style={sectionTitleStyle}>Remote Desktop Settings</Typography>
                                                        </Box>
                                                    }
                                                    style={cardHeaderStyle}
                                                />
                                                <CardContent>
                                                    <Box display="flex" justifyContent="space-between">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isVncTokenAuthentication}
                                                                    onChange={event => {
                                                                        const newValue = event.target.checked; 
                                                                        setIsVncTokenAuthentication(newValue); 
                                                                        setForm(prevForm => ({
                                                                            ...prevForm,
                                                                            info: {
                                                                                ...prevForm.info,
                                                                                vncTokenAuthentication: newValue 
                                                                            }
                                                                        }));
                                                                    }}
                                                                    name="vncTokenAuthentication"
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Token Authentication"
                                                        />
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isVncRemoteClient}
                                                                    onChange={event => {
                                                                        const newValue = event.target.checked;
                                                                        setIsVncRemoteClient(newValue);
                                                                        setForm(prevForm => ({
                                                                            ...prevForm,
                                                                            info: {
                                                                                ...prevForm.info,
                                                                                vncRemoteClient: newValue
                                                                            }
                                                                        }));
                                                                    }}
                                                                    name="vncRemoteClient"
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Remote Client"
                                                        />
                                                    </Box>
                                                    <DcvCheckbox isDcvEnabled={isDcvEnabled} setIsDcvEnabled={setIsDcvEnabled} setForm={setForm} clusterSettings={clusterSettings} orgClusterSettings={orgClusterSettings} provider="ibm" />
                                                </CardContent>
                                            </Card>
                                        </div>
                                        
                                        {/* Right column - Provisioning Script (hidden for marketplace apps with hide_provision_script) */}
                                        {!marketplaceHideProvisionScript && (
                                        <div style={columnStyle}>
                                            <Card style={{...cardStyle, height: '100%'}}>
                                                <CardHeader
                                                    title={
                                                        <Box display="flex" alignItems="center">
                                                            <Icon style={{marginRight: '8px', color: '#2196F3'}}>code</Icon>
                                                            <Typography style={sectionTitleStyle}>Provisioning Script</Typography>
                                                        </Box>
                                                    }
                                                    style={cardHeaderStyle}
                                                />
                                                <CardContent>
                                                    <div className="flex items-center mb-2">
                                                        <div className="flex-grow">
                                                            <input
                                                                className={classes.uploadInput}
                                                                onChange={e => handleUpload(e)}
                                                                id="ibm_upload-btn"
                                                                type="file"
                                                                disabled={disableScriptModification}
                                                            />
                                                            <label htmlFor="ibm_upload-btn">
                                                                <Button
                                                                    id="ibm_upload-label"
                                                                    component="span"
                                                                    variant="contained"
                                                                    className={classes.grayButton}
                                                                    startIcon={<Icon>cloud_upload</Icon>}
                                                                    disabled={disableScriptModification}
                                                                >
                                                                    Upload Script
                                                                </Button>
                                                            </label>
                                                        </div>
                                                        
                                                        {!form.data.provision?.script ? (
                                                            <Typography variant="body2" color="textSecondary">
                                                                No Script
                                                            </Typography>
                                                        ) : (
                                                            <div className="flex items-center">
                                                                <Chip 
                                                                    label="Script Uploaded" 
                                                                    color="primary" 
                                                                    size="small"
                                                                    style={{marginRight: '8px'}}
                                                                />
                                                                {!disableScriptModification && (
                                                                    <IconButton size="small" onClick={handleDeleteScript}>
                                                                        <Icon fontSize="small">delete</Icon>
                                                                    </IconButton>
                                                                )}
                                                                <IconButton id="ibm_script-actions-view-button" size="small" onClick={() => setScriptOpen(true)}>
                                                                    <Icon fontSize="small">visibility</Icon>
                                                                </IconButton>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                        )}
                                    </div>
                                )}

                                {/* Global Provision Scripts Preview - Feature 002 Enhancement */}
                                {/* Always render when disable_global_scripts is set (so configs load for auto-exclude), but hide the UI */}
                                {(advanced || marketplaceDisableGlobalScripts) && (
                                    <div style={marketplaceDisableGlobalScripts ? { display: 'none' } : undefined}>
                                        <GlobalProvisionScriptsPreview
                                            provider="ibm"
                                            osType={form?.info?.['os-type'] || ''}
                                            clusterName={form?.name || ''}
                                            clusterId={form?.id}
                                            excludedScripts={excludedGlobalScripts}
                                            onExcludedScriptsChange={setExcludedGlobalScripts}
                                            disabled={disableInputs || marketplaceDisableGlobalScripts}
                                        />
                                    </div>
                                )}

                                {/* Cluster Applications - visible in both Basic and Advanced modes when apps exist */}
                                {(advanced || clusterApplications.length > 0) && (
                                    <ClusterApplicationsSection
                                        applications={clusterApplications}
                                        onChange={setClusterApplications}
                                        disabled={disableInputs}
                                        cardStyle={cardStyle}
                                        cardHeaderStyle={cardHeaderStyle}
                                        sectionTitleStyle={sectionTitleStyle}
                                    />
                                )}

                                {/* Network Settings and Fast Capacity Validation - Side by Side */}
                                {advanced && (
                                    <div style={columnContainerStyle}>
                                        {/* Left column - Network Settings */}
                                        <div style={columnStyle}>
                                            <Card style={{...cardStyle, height: '100%'}}>
                                                <CardHeader
                                                    title={
                                                        <Box display="flex" alignItems="center">
                                                            <Icon style={{marginRight: '8px', color: '#2196F3'}}>cloud_queue</Icon>
                                                            <Typography style={sectionTitleStyle}>Network Settings</Typography>
                                                        </Box>
                                                    }
                                                    style={cardHeaderStyle}
                                                />
                                                <CardContent>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={enableNodeInternetAccess}
                                                                onChange={event => {
                                                                    const newValue = event.target.checked;
                                                                    setEnableNodeInternetAccess(newValue);
                                                                    setForm(prevForm => {
                                                                        const updated = {
                                                                            ...prevForm,
                                                                            data: {
                                                                                ...prevForm.data,
                                                                                network: {
                                                                                    ...prevForm.data.network,
                                                                                    enable_node_internet_access: newValue
                                                                                }
                                                                            }
                                                                        };
                                                                        return updated;
                                                                    });
                                                                }}
                                                                name="enableNodeInternetAccess"
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="body1" style={{fontWeight: 500}}>
                                                                    Enable Compute Node Internet Access
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary" style={{display: 'block', marginTop: '4px'}}>
                                                                    Attach public gateway for outbound internet connectivity while maintaining security isolation.
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </CardContent>
                                            </Card>
                                        </div>

                                        {/* Right column - Fast Capacity Validation */}
                                        <div style={columnStyle}>
                                            <Card style={{...cardStyle, height: '100%'}}>
                                                <CardHeader
                                                    title={
                                                        <Box display="flex" alignItems="center">
                                                            <Icon style={{marginRight: '8px', color: '#2196F3'}}>speed</Icon>
                                                            <Typography style={sectionTitleStyle}>Fast Capacity Validation</Typography>
                                                        </Box>
                                                    }
                                                    style={cardHeaderStyle}
                                                />
                                                <CardContent>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={capacityReservationParallelDeployment}
                                                                onChange={(e) => {
                                                                    const newValue = e.target.checked;
                                                                    setEnableCapacityReservation(newValue);
                                                                    setCapacityReservationParallelDeployment(newValue);
                                                                    setForm(prevForm => ({
                                                                        ...prevForm,
                                                                        data: {
                                                                            ...prevForm.data,
                                                                            network: {
                                                                                ...prevForm.data.network,
                                                                                enable_capacity_reservation: newValue,
                                                                                capacity_reservation_parallel_deployment: newValue
                                                                            }
                                                                        }
                                                                    }));
                                                                }}
                                                                name="capacityReservationParallelDeployment"
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="body1" style={{fontWeight: 500}}>
                                                                    Parallel Resources Provisioning
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary" style={{display: 'block', marginTop: '4px'}}>
                                                                    Detects capacity issues faster and reduces total deployment time.
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </CardContent>
                                            </Card>
                                        </div>
                                    </div>
                                )}

                                {/* Storage Settings */}
                                {advanced && (
                                    <Card style={cardStyle}>
                                        <CardHeader
                                            title={
                                                <Box display="flex" alignItems="center">
                                                    <Icon style={{marginRight: '8px', color: '#2196F3'}}>storage</Icon>
                                                    <Typography style={sectionTitleStyle}>Storage Settings</Typography>
                                                </Box>
                                            }
                                            style={cardHeaderStyle}
                                        />
                                        <CardContent>
                                            <div style={inputGroupStyle}>
                                                <TextFieldFormsy
                                                    className={clsx(classes.input)}
                                                    style={{flex: 1}}
                                                    id="ibm_server-volume_size"
                                                    name="volume_size"
                                                    label="Server Volume (GB)"
                                                    type="text"
                                                    variant="outlined"
                                                    value={form.data?.server?.volume_size || ''}
                                                    onChange={e => setServerObj(e.target)}
                                                    placeholder={`Disk size of the Cluster Head Node (Min: ${providerVolsize.min}, Max: ${providerVolsize.max})`}
                                                />
                                                <TextFieldFormsy
                                                    className={clsx(classes.input)}
                                                    style={{flex: 1}}
                                                    id="ibm_node-volume_size"
                                                    name="volume_size"
                                                    label="Node Volume (GB)"
                                                    type="text"
                                                    variant="outlined"
                                                    value={form.data?.node?.volume_size || ''}
                                                    onChange={e => setNodeObj(e.target)}
                                                    placeholder={`Disk size of the Cluster Nodes (Min: ${providerVolsize.min}, Max: ${providerVolsize.max})`}
                                                />
                                                <SelectFormsy
                                                    className={clsx(classes.input)}
                                                    style={{flex: 1}}
                                                    id="ibm_share"
                                                    name="share"
                                                    label="Shared File System"
                                                    type="select"
                                                    variant="outlined"
                                                    value={form.data?.share || 'nfs'}
                                                    onChange={e => setDataObj(e.target)}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: { maxHeight: 300 }
                                                        }
                                                    }}
                                                >
                                                    <MenuItem id="ibm_nfs" value="nfs">
                                                        NFS
                                                    </MenuItem>
                                                </SelectFormsy>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Storage Configuration - Only show in advanced mode */}
                                {advanced && (
                                    <ClusterStorageConfig
                                        value={storageConfiguration}
                                        onChange={handleStorageConfigurationChange}
                                        disabled={disableInputs}
                                        cloudProvider="ibm"
                                    />
                                )}

                                {/* Head Node Performance Data Storage - Only show if storage configs are available */}
                                {availableStorageConfigs && availableStorageConfigs.length > 0 && (
                                    <Card style={cardStyle}>
                                        <CardHeader
                                            title={
                                                <Box display="flex" alignItems="center">
                                                    <Icon style={{marginRight: '8px', color: '#2196F3'}}>storage</Icon>
                                                    <Typography style={sectionTitleStyle}>Head Node Performance Data Storage</Typography>
                                                </Box>
                                            }
                                            style={cardHeaderStyle}
                                        />
                                        <CardContent>
                                            <Typography variant="body2" color="textSecondary" style={{marginBottom: '16px'}}>
                                                Attach high-performance storage to head node.
                                            </Typography>

                                            {/* Display existing head node volumes with inline switch */}
                                            {form.data?.server?.attachedVolumes && form.data.server.attachedVolumes.length > 0 ? (
                                                <Box mb={2}>
                                                    {form.data.server.attachedVolumes.map((volume, volIndex) => {
                                                        if (!volume) return null;
                                                        const storageConfig = availableStorageConfigs.find(config => config.id === volume.storageConfigId);
                                                        const storageConfigName = storageConfig?.name || 'Unknown Storage';
                                                        
                                                        // Get capacity with priority: volume.size > volume.overrides.capacity > headVolume.overrides.capacity > policy.ibmParams.capacity
                                                        const headVolume = storageConfig?.headNodeVolumes?.[0];
                                                        const policyId = headVolume?.policy;
                                                        const policy = policyId ? policies.find(p => p._id === policyId) : null;
                                                        
                                                        const displaySize = volume.size || 
                                                                           volume.overrides?.capacity || 
                                                                           headVolume?.overrides?.capacity ||
                                                                           policy?.ibmParams?.capacity ||
                                                                           null;
                                                        
                                         
                                                        return (
                                                            <Box 
                                                                key={volume.id || `volume-${volIndex}`}
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                mb={1}
                                                            >
                                                                {/* Blue rounded chip with volume info and edit/delete icons */}
                                                                <div style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    backgroundColor: '#E3F2FD',
                                                                    borderRadius: '16px',
                                                                    padding: '4px 10px',
                                                                    height: '26px',
                                                                    border: '1px solid #BBDEFB',
                                                                    marginRight: '8px',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '8px' }}>
                                                                        <Typography variant="body2" style={{
                                                                            fontSize: '12px',
                                                                            fontWeight: 500,
                                                                            color: '#1565C0',
                                                                            maxWidth: '200px',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap'
                                                                        }}>
                                                                            {displaySize 
                                                                                ? `${storageConfigName} (${displaySize}GB)`
                                                                                : storageConfigName
                                                                            }
                                                                        </Typography>
                                                                    </div>
                                                                    
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                                        {/* Edit Icon inside chip */}
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openHeadVolumeEditDialog(volume);
                                                                            }}
                                                                            style={{
                                                                                padding: '1px',
                                                                                width: '16px',
                                                                                height: '16px',
                                                                                color: '#1976D2',
                                                                                backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                                                            }}
                                                                        >
                                                                            <Icon style={{ fontSize: '11px' }}>edit</Icon>
                                                                        </IconButton>
                                                                        
                                                                        {/* Delete Icon inside chip */}
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                removeVolumeFromHeadNode(volume.id);
                                                                            }}
                                                                            style={{
                                                                                padding: '1px',
                                                                                width: '16px',
                                                                                height: '16px',
                                                                                color: '#D32F2F',
                                                                                backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                                                            }}
                                                                        >
                                                                            <Icon style={{ fontSize: '11px' }}>delete</Icon>
                                                                        </IconButton>
                                                                    </div>
                                                                </div>

                                                                {/* NFS sharing toggle outside chip on the right */}
                                                                <Box display="flex" alignItems="center" style={{ marginLeft: '16px', gap: '12px' }}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={volume.nfsEnabled || false}
                                                                                onChange={(e) => {
                                                                                    const updatedForm = _.cloneDeep(form);
                                                                                    if (updatedForm.data.server.attachedVolumes[volIndex]) {
                                                                                        updatedForm.data.server.attachedVolumes[volIndex].nfsEnabled = e.target.checked;
                                                                                        setForm(updatedForm);
                                                                                    }
                                                                                }}
                                                                                color="primary"
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Typography variant="body1" style={{fontWeight: 500, fontSize: '14px'}}>
                                                                                Share via NFS
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => setPdsInfoPopoverAnchorEl(e.currentTarget)}
                                                                    >
                                                                        <Icon style={{fontSize: '20px', color: '#2196F3'}}>info</Icon>
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            ) : (
                                                <Box mb={2} p={2} style={{backgroundColor: '#f5f5f5', borderRadius: '4px', border: '1px dashed #e0e0e0'}}>
                                                    <Typography variant="body2" color="textSecondary" style={{fontStyle: 'italic'}}>
                                                        No volumes attached
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Info Popover */}
                                            <Popover
                                                open={pdsInfoPopoverOpen}
                                                anchorEl={pdsInfoPopoverAnchorEl}
                                                onClose={() => setPdsInfoPopoverAnchorEl(null)}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                            >
                                                <Box p={2} style={{maxWidth: '400px'}}>
                                                    <Typography variant="subtitle2" style={{fontWeight: 600, marginBottom: '8px'}}>
                                                        Performance Data Storage
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        When enabled, this volume will be automatically shared with all compute nodes via NFS (Network File System). The volume will be mounted at the same path on all nodes, allowing for shared high-performance data access across your cluster. This is ideal for:
                                                    </Typography>
                                                    <ul style={{marginTop: '8px', marginBottom: '8px', paddingLeft: '20px'}}>
                                                        <li><Typography variant="body2" color="textSecondary">Shared datasets and input files</Typography></li>
                                                        <li><Typography variant="body2" color="textSecondary">Intermediate computation results</Typography></li>
                                                        <li><Typography variant="body2" color="textSecondary">Checkpoints and logs</Typography></li>
                                                        <li><Typography variant="body2" color="textSecondary">Collaborative workspaces</Typography></li>
                                                    </ul>
                                                    <Typography variant="body2" color="textSecondary">
                                                        All nodes will have read/write access to the shared storage.
                                                    </Typography>
                                                </Box>
                                            </Popover>

                                            {/* Add Volume Button - Only show if no volume exists (max 1) */}
                                            {(!form.data.server.attachedVolumes || form.data.server.attachedVolumes.length === 0) && (
                                                <Box>
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<Icon>add</Icon>}
                                                        onClick={handleHeadNodeVolumeDropdownOpen}
                                                        disabled={availableStorageConfigs.length === 0}
                                                    >
                                                        Add Volume
                                                    </Button>

                                                    {/* Storage Config Selection Popover */}
                                                    <Popover
                                                        open={headNodeVolumeDropdownOpen}
                                                        anchorEl={headNodeVolumeDropdownAnchorEl}
                                                        onClose={handleHeadNodeVolumeDropdownClose}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'left',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'left',
                                                        }}
                                                        PaperProps={{
                                                            style: {
                                                                maxHeight: 300,
                                                                minWidth: 250
                                                            }
                                                        }}
                                                    >
                                                        {availableStorageConfigs.map((config) => (
                                                            <MenuItem 
                                                                key={config.id} 
                                                                onClick={() => handleHeadVolumeConfigSelect(config.id)}
                                                            >
                                                                <Box display="flex" alignItems="center">
                                                                    <Icon style={{ marginRight: 8, fontSize: '16px' }}>storage</Icon>
                                                                    <Box>
                                                                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                                                                            {config.name || config.providerName || 'Unknown Provider'}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary" style={{ fontSize: '10px' }}>
                                                                            {config.providerType ? config.providerType.toUpperCase() : 'UNKNOWN'}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                        {availableStorageConfigs.length === 0 && (
                                                            <MenuItem disabled>
                                                                No storage providers available
                                                            </MenuItem>
                                                        )}
                                                    </Popover>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Compute Nodes Card - Full Width */}
                                <Card style={cardStyle}>
                                    <CardHeader
                                        title={
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Box display="flex" alignItems="center">
                                                    <Icon style={{marginRight: '8px', color: '#2196F3'}}>device_hub</Icon>
                                                    <Typography style={sectionTitleStyle}>Compute Nodes</Typography>
                                                    <Tooltip title="The available Instance Sizes and quantity of Instances may vary based on your provider account status.">
                                                        <Icon style={tooltipIconStyle}>help_outline</Icon>
                                                    </Tooltip>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<Icon>add</Icon>}
                                                    onClick={addComputeNode}
                                                    disabled={form.data.node.group.length >= 10 || disableInputs}
                                                    className={classes.grayButton}
                                                >
                                                    Add Node Group
                                                </Button>
                                            </Box>
                                        }
                                        style={cardHeaderStyle}
                                    />
                                    <CardContent>
                                        {nodeInstanceSizes && form.data.node.group.map((group, index) => (
                                            <Paper key={`node-group-${index}-${group.count}`} style={{ padding: '16px', marginBottom: '16px', position: 'relative' }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                                        Node Group {index + 1}
                                                    </Typography>
                                                    {form.data.node.group.length > 1 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeComputeNode(index)}
                                                            disabled={disableInputs}
                                                            style={{ color: '#f44336' }}
                                                        >
                                                            <Icon>delete</Icon>
                                                        </IconButton>
                                                    )}
                                                </Box>

                                                {/* Single Row: Node Stack | Instance Type | Image Type | Count */}
                                                <div style={{
                                                    display: 'grid', 
                                                    gridTemplateColumns: imageMode === IMAGE_MODE.MULTI_IMAGE 
                                                        ? '18% 49% 18% 10%' 
                                                        : '55% 25% 15%', 
                                                    gap: '16px', 
                                                    alignItems: 'center',
                                                    width: '100%'
                                                }}>
                                                    {imageMode === IMAGE_MODE.MULTI_IMAGE && (
                                                        <Autocomplete
                                                            freeSolo
                                                            disabled={disableInputs}
                                                            options={filteredStacks}
                                                            getOptionLabel={(option) => option.name || option}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Node Stack"
                                                                    placeholder="Select or enter stack name"
                                                                    variant="outlined"
                                                                    disabled={disableInputs}
                                                                />
                                                            )}
                                                            onChange={(event, newValue) => {
                                                                if (typeof newValue === 'string') {
                                                                    updateNodeGroup(index, 'image_name', newValue);
                                                                } else if (newValue) {
                                                                    updateNodeGroup(index, 'image_name', newValue.name);
                                                                }
                                                            }}
                                                            value={group.image_name || ''}
                                                        />
                                                    )}

                                                    <TextField
                                                        select
                                                        name={`instance_type_${index}`}
                                                        label="Instance Type"
                                                        value={group.instance_id || ''}
                                                        onChange={(e) => updateNodeGroup(index, 'instance_id', e.target.value)}
                                                        required
                                                        variant="outlined"
                                                        disabled={disableInputs}
                                                        SelectProps={{
                                                            displayEmpty: true,
                                                            renderValue: (selected) => {

                                                                const instance = nodeInstanceSizes?.find(i => i.instance_id === selected);
                                                                if (instance) {
                                                                    return instance.gpus 
                                                                        ? `${instance.name} - GPUs: ${instance.gpus}`
                                                                        : instance.name;
                                                                }
                                                                return selected;
                                                            }
                                                        }}
                                                    >

                                                        {nodeInstanceSizes ? nodeInstanceSizes.map((instance) => (
                                                            <MenuItem key={instance.instance_id} value={instance.instance_id}>
                                                                {instance.name}
                                                                {instance.gpus && ` - GPUs: ${instance.gpus}`}
                                                            </MenuItem>
                                                        )) : []}
                                                    </TextField>

                                                    <TextField
                                                        select
                                                        id={`ibm_node_image_type_${index}`}
                                                        name={`node_image_type_${index}`}
                                                        label="Image Type"
                                                        variant="outlined"
                                                        value={nodeImageTypes[index] || IMAGE_TYPE.HEIDI_STACK}
                                                        onChange={(e) => {
                                                            const newTypes = { ...nodeImageTypes };
                                                            newTypes[index] = e.target.value;
                                                            setNodeImageTypes(newTypes);
                                                            updateNodeGroup(index, 'image_type', e.target.value);
                                                        }}
                                                        disabled={disableInputs}
                                                    >
                                                        <MenuItem value={IMAGE_TYPE.HEIDI_STACK}>Heidi Stack</MenuItem>
                                                        <MenuItem value={IMAGE_TYPE.CUSTOM}>Custom Image</MenuItem>
                                                    </TextField>

                                                    <TextField
                                                        key={`count-${index}-${group.count}-${form?.id}`}
                                                        label="Count"
                                                        type="number"
                                                        value={group.count != null ? group.count : 1}
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value, 10);
                                                            if (!isNaN(value) && value >= 0) {
                                                                updateNodeGroup(index, 'count', value);
                                                            }
                                                        }}
                                                        variant="outlined"
                                                        InputProps={{
                                                            inputProps: { min: 0, max: 100 }
                                                        }}
                                                        disabled={disableInputs}
                                                    />
                                                </div>

                                                {/* Custom Image ID field - only show when Custom Image is selected */}
                                                {nodeImageTypes[index] === IMAGE_TYPE.CUSTOM && (
                                                    <div style={{ marginTop: '16px' }}>
                                                        <TextField
                                                            label="Custom Image ID"
                                                            value={group.image_id || ''}
                                                            onChange={(e) => updateNodeGroup(index, 'image_id', e.target.value)}
                                                            variant="outlined"
                                                            disabled={disableInputs}
                                                            fullWidth
                                                        />
                                                    </div>
                                                )}

                                                {/* Volume Management - only show if storage configs are available */}
                                                {availableStorageConfigs.length > 0 && (
                                                    <Box mt={2}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                                                Volumes
                                                            </Typography>
                                                            {/* Add Volume button */}
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<Icon style={{ fontSize: '14px' }}>add</Icon>}
                                                                onClick={(e) => handleVolumeDropdownOpen(e, index)}
                                                                style={{
                                                                    fontSize: '11px',
                                                                    padding: '4px 8px',
                                                                    minWidth: 'auto',
                                                                    height: '28px'
                                                                }}
                                                            >
                                                                Add Volume
                                                            </Button>
                                                            
                                                            {/* Dropdown menu */}
                                                            <Menu
                                                                anchorEl={volumeDropdownAnchorEl.current[index]}
                                                                open={Boolean(volumeDropdownOpen[index])}
                                                                onClose={() => handleVolumeDropdownClose(index)}
                                                                PaperProps={{
                                                                    style: {
                                                                        maxHeight: 300,
                                                                        minWidth: 250
                                                                    }
                                                                }}
                                                            >
                                                                {availableStorageConfigs.map((config) => (
                                                                    <MenuItem 
                                                                        key={config.id} 
                                                                        onClick={() => handleVolumeConfigSelect(index, config.id)}
                                                                    >
                                                                        <Box display="flex" alignItems="center">
                                                                            <Icon style={{ marginRight: 8, fontSize: '16px' }}>storage</Icon>
                                                                            <Box>
                                                                                <Typography variant="body2" style={{ fontSize: '12px' }}>
                                                                                    {config.name || config.providerName || 'Unknown Provider'}
                                                                                </Typography>
                                                                                <Typography variant="caption" color="textSecondary" style={{ fontSize: '10px' }}>
                                                                                    {config.providerType ? config.providerType.toUpperCase() : 'UNKNOWN'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </MenuItem>
                                                                ))}
                                                                {availableStorageConfigs.length === 0 && (
                                                                    <MenuItem disabled>
                                                                        No storage providers available
                                                                    </MenuItem>
                                                                )}
                                                            </Menu>
                                                        </Box>
                                                        <VolumeManager
                                                            volumes={group.attachedVolumes || []}
                                                            availableStorageConfigs={availableStorageConfigs}
                                                            onVolumeUpdate={handleVolumeUpdate}
                                                            onVolumeDelete={removeVolumeFromNode}
                                                            nodeGroupIndex={index}
                                                            chipContainerStyle={{
                                                                backgroundColor: 'transparent',
                                                                padding: '12px',
                                                                borderRadius: '4px',
                                                                border: '1px solid #e0e0e0',
                                                                minHeight: '40px'
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </Paper>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Formsy>
                        </DisabledFormWrapper>
                    </DialogContent>

                    {isScriptOpen && (
                        <ScriptDialog 
                            open={isScriptOpen}
                            onClose={() => setScriptOpen(false)} 
                            scriptContent={form.data.provision?.script}
                        />
                    )}
                    
                    {/* Head node volume details dialog */}
                    <IbmVolumeDetailsDialog
                        open={headDialogOpen}
                        onClose={closeHeadVolumeDialog}
                        onSave={saveHeadVolumeDialog}
                        volume={headSelectedVolume}
                        storageConfig={headSelectedStorageConfig}
                        policy={headSelectedPolicy}
                        isEditable={headIsEditable}
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default IbmCluster;
